import BannerPictureComponent, {
  BannerPicture,
  PictureBreakpoints
} from '../banner-picture/banner-picture.component';
import LinkButtonComponent, {
  Icon
} from '@spa/components/button/link-button/link-button.component';
import LinkComponent, {
  LinkComponentLink,
  getNoTrackIntersectionLink
} from '../link/link.component';
import { ReactElement, memo } from 'react';
import { ButtonType } from '@spa/components/button/types/ButtonType';
import { CmsComponentContext } from '../container/CmsComponentContext';
import ConfigurableCmsValidity from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-validity.component';
import HeadlineComponent from '@spa/components/headline/headline.component';
import { IconSize } from '@spa/components/button/button/button.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import classNames from 'clsx';
import styles from './banner.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';

export interface LinkCta {
  readonly type: 'link';
  readonly link: LinkComponentLink;
}

export interface Button {
  readonly text: string;
  readonly style: ButtonType;
  readonly link: LinkComponentLink;
}

export interface ButtonsCta {
  readonly type: 'buttons';
  readonly buttons: ReadonlyArray<Button>;
}

export type Cta = LinkCta | ButtonsCta;

export interface Props {
  readonly id?: string;
  readonly cta: Cta | null;
  readonly images: BannerPicture;
  readonly priority: boolean | null;
  readonly enableNextJsLoader?: boolean;
  readonly headline: string | null;
  readonly subHeadline: string | null;
  readonly headlineLink: LinkComponentLink | null;
  readonly bannerHeadline?: ReadonlyArray<string>;
  readonly bannerSubHeadline?: string | null;
  readonly context: CmsComponentContext;
  readonly scaleImageBeyondIntrinsicDimensions?: boolean;
  readonly editorMode?: boolean;
  readonly dateTo: number | null;
  readonly dateFrom: number | null;
}

const MAX_RENDERED_BUTTONS: number = 3;

function BannerComponent({
  id,
  images,
  cta,
  headline,
  subHeadline,
  headlineLink,
  bannerHeadline,
  bannerSubHeadline,
  priority,
  enableNextJsLoader = true,
  context,
  scaleImageBeyondIntrinsicDimensions = true,
  editorMode = false,
  dateTo,
  dateFrom
}: Props): ReactElement {
  const { formatMessage }: NamespacedIntl = useNamespacedIntl('banner');
  const columnLayout: boolean = context === CmsComponentContext.FLEXGRID;

  const imageElm: ReactElement = (
    <BannerPictureComponent
      picture={images}
      priority={priority}
      enableNextJsLoader={enableNextJsLoader}
      fixedBreakPoint={PictureBreakpoints.MOBILE}
      scaleImageBeyondIntrinsicDimensions={scaleImageBeyondIntrinsicDimensions}
    />
  );

  let bannerLink: LinkComponentLink | null = null;
  let buttons: ReadonlyArray<Button> = [];
  if (cta !== null) {
    switch (cta.type) {
      case 'link':
        bannerLink = cta.link;
        break;
      case 'buttons':
        buttons = cta.buttons;
        break;
    }
  }

  const buildBannerButtons = (): Array<ReactElement> => {
    const result: Array<ReactElement> = [];
    if (buttons.length !== 0) {
      const maxButtons: number =
        MAX_RENDERED_BUTTONS < buttons.length ? MAX_RENDERED_BUTTONS : buttons.length;
      for (let buttonsIdx = 0; buttonsIdx < maxButtons; buttonsIdx++) {
        const icon: Icon = {
          src: '/assets/icons/arrow_forward_orange.svg',
          height: 24,
          width: 24,
          alt: formatMessage('iconAlt'),
          unoptimized: true
        };

        const { text, style, link } = buttons[buttonsIdx];
        result.push(
          <div key={`${text}_${buttonsIdx}`} className={styles['banner__button']}>
            <LinkButtonComponent
              buttonType={style}
              link={(bannerLink && getNoTrackIntersectionLink(link)) || link}
              title={link.linkTitle ?? undefined}
              text={text}
              iconRight={icon}
              iconSize={IconSize.EXTRA_SMALL}
            />
          </div>
        );
      }
    }
    return result;
  };

  const bannerButtons: Array<ReactElement> = buildBannerButtons();

  const buildHeadlines = (source: ReadonlyArray<string>): Array<ReactElement> => {
    return source.map(
      (line: string): ReactElement => (
        <span className={styles['banner__headline']} key={line}>
          {line}
        </span>
      )
    );
  };

  const bannerStyles = classNames(styles['banner'], styles[`banner--context-${context}`]);

  const headlineComponent =
    bannerHeadline?.[0] && columnLayout ? (
      <HeadlineComponent
        id={`${id}-banner-headline`}
        headline={bannerHeadline[0]}
        subHeadline={bannerSubHeadline ?? null}
        link={null}
        context={CmsComponentContext.BANNER}
        ariaLabelledby={bannerLink === null ? undefined : id}
      />
    ) : (
      bannerHeadline &&
      bannerHeadline.length !== 0 && (
        <div className={styles['banner__headline-wrapper']}>
          {buildHeadlines(bannerHeadline)}
          {bannerSubHeadline && (
            <span className={styles['banner__sub-headline']}>{bannerSubHeadline}</span>
          )}
        </div>
      )
    );

  return (
    <div className={styles['banner']}>
      {editorMode && <ConfigurableCmsValidity dateFrom={dateFrom} dateTo={dateTo} />}
      {headline && (
        <HeadlineComponent
          id={`${id}-headline`}
          headline={headline}
          subHeadline={subHeadline}
          link={getNoTrackIntersectionLink(headlineLink)}
        />
      )}
      <article className={bannerStyles}>
        {bannerLink !== null ? (
          <LinkComponent link={bannerLink} className={styles['banner__link']}>
            {imageElm}
            {headlineComponent}
          </LinkComponent>
        ) : (
          <>
            {imageElm}
            {headlineComponent}
          </>
        )}
        {bannerButtons.length !== 0 && (
          <div className={styles['banner__buttons']}>{bannerButtons}</div>
        )}
      </article>
    </div>
  );
}

export default memo(BannerComponent);
